/**
 * The typed client for `/api/v1`. Every call carries a fresh access token.
 * Response types come from the generated contract (src/api/schema.ts), and
 * each request body `satisfies` its schema there, so a renamed or missing
 * field fails `tsc`, not a request (Idea #305).
 */
import { PRIVACY_CHANGED, PRIVACY_HEADER, type PrivacyStore, privacyStore } from "../lib/privacy";
import type { components, operations } from "./schema";
import type {
  ActiveCall,
  ActivityMove,
  AddedEmailDomain,
  AddedNetworkAddress,
  AppSettings,
  AutotaskChoices,
  AutotaskResourceSet,
  BackupAsked,
  BackupCheck,
  BackupList,
  BackupRequest,
  BillingType,
  BlockDraftSaved,
  BlockDuration,
  BlockMerge,
  BlockSplit,
  BlockState,
  CallChosen,
  Capture,
  CaptureMailbox,
  CaptureSource,
  ClientAssignment,
  ClientAudit,
  Grouping,
  ManagedEffort,
  ClientChanges,
  ClientChoice,
  ClientEmailDomains,
  ClientMapping,
  ClientNetworkAddresses,
  ClientPhones,
  DemoCopy,
  ClientRecord,
  DattoSites,
  DattoSiteSet,
  DattoUsernameSet,
  Day,
  DismissReason,
  EmailPreviewKind,
  EmailPreviewQueued,
  Integrations,
  Lane,
  Me,
  OfficeAddress,
  OpenTicket,
  OpenTicketsReport,
  OwnerJob,
  OwnerJobKey,
  Queued,
  ReadMailSet,
  ReportFilters,
  Retried,
  RingingCall,
  SavedTheme,
  ScreenConnectCompanies,
  ScreenConnectCompanySet,
  ScreenConnectUsernameSet,
  SettingsChanges,
  SkySwitchExtensionsSet,
  StartedOver,
  Submission,
  SubmissionPreview,
  SubmitChoices,
  TechActivityReport,
  Team,
  TicketState,
  TechMapping,
  TicketAction,
  TicketCloses,
  TicketTray,
  UnsubmittedAgingReport,
  UtilizationReport,
  UserChanges,
  UserSummary,
  VendorMarked,
  VpnAddress,
  WorkComputersSet,
} from "./types";

type Schemas = components["schemas"];
// Every report takes the same filters (ADR 0123): a query fit for one is fit for each.
type ReportQuery = operations["tech_activity_api_v1_reports_tech_activity_get"]["parameters"]["query"] &
  operations["client_audit_api_v1_reports_client_audit_get"]["parameters"]["query"];
type AgingQuery = NonNullable<
  operations["unsubmitted_aging_api_v1_reports_unsubmitted_aging_get"]["parameters"]["query"]
>;

function appendIds(params: URLSearchParams, query: { user_id: string[]; client_id: string[] }): string {
  for (const id of query.user_id) {
    params.append("user_id", id);
  }
  for (const id of query.client_id) {
    params.append("client_id", id);
  }
  return params.toString();
}

/** A report's filters as its query string; a name the API doesn't take fails `tsc`. */
function reportQuery(filters: ReportFilters): string {
  const query = {
    start: filters.start,
    end: filters.end,
    user_id: [...filters.userIds],
    client_id: [...filters.clientIds],
  } satisfies ReportQuery;
  return appendIds(new URLSearchParams({ start: query.start, end: query.end }), query);
}

type UtilizationQuery = NonNullable<
  operations["utilization_report_api_v1_reports_utilization_get"]["parameters"]["query"]
>;

/** The utilization report's query: the shared filters, by day, week or month. */
function utilizationQuery(filters: ReportFilters, by: Grouping): string {
  const params = new URLSearchParams(reportQuery(filters));
  params.set("by", by satisfies UtilizationQuery["by"]);
  return params.toString();
}

/** The unsubmitted aging report's query: as of now, so the techs and clients alone. */
function agingQuery(filters: ReportFilters): string {
  const query = {
    user_id: [...filters.userIds],
    client_id: [...filters.clientIds],
  } satisfies AgingQuery;
  return appendIds(new URLSearchParams(), query);
}

type OpenTicketsQuery = NonNullable<
  operations["open_tickets_api_v1_reports_open_tickets_get"]["parameters"]["query"]
>;

/**
 * The Open tickets report's query (ADR 0185): as of now, so the techs and
 * clients alone, and the states asked for (none: every one but closed).
 */
function openTicketsQuery(filters: ReportFilters, states: readonly TicketState[]): string {
  const query = {
    user_id: [...filters.userIds],
    client_id: [...filters.clientIds],
    state: [...states],
  } satisfies OpenTicketsQuery;
  const params = new URLSearchParams();
  for (const state of query.state) {
    params.append("state", state);
  }
  return appendIds(params, query);
}

/** One field the server refused, from a 422's `detail` list. */
export interface FieldError {
  /** The field's name: the first element of the server's `loc`. */
  field: string;
  message: string;
  /** Which of a list's items, when the second element of `loc` says (`["numbers", 0]`). */
  index?: number;
}

/** A non-2xx answer from the API, with the server's `detail` when it sent one. */
export class ApiError extends Error {
  override name = "ApiError";

  constructor(
    readonly status: number,
    readonly detail: string | null,
    readonly fieldErrors: readonly FieldError[] = [],
  ) {
    super(detail ?? `The server answered ${String(status)}.`);
  }
}

/** For a query: retry only what might succeed next time, not a refusal the server meant. */
export function retryUnlessRefused(failures: number, error: Error): boolean {
  return !(error instanceof ApiError && error.status < 500) && failures < 2;
}

export interface ApiClient {
  getMe(): Promise<Me>;
  /** Record that a session started (audit log). Returns the user, like getMe. */
  recordSignIn(): Promise<Me>;
  /** Save the signed-in user's theme choice, so it follows them (ADR 0041). */
  setTheme(theme: SavedTheme): Promise<Me>;
  /** A local day (YYYY-MM-DD): the signed-in user's own, or (owners only) `userId`'s. */
  getDay(day: string, userId?: string | null): Promise<Day>;
  /** Owner only. Everyone, for the user switcher. */
  listUsers(): Promise<UserSummary[]>;
  /** A person's time zone: an owner's anyone's, a user their own on My settings (ADRs 0172 and 0200). */
  updateUser(id: string, changes: UserChanges): Promise<UserSummary>;
  /** Owner only. Everyone by their last 14 days, each with the day view's numbers. */
  getTeam(): Promise<Team>;
  /** The clients anyone can choose for a block, by name. */
  listClientChoices(): Promise<ClientChoice[]>;
  /** A client's open Autotask tickets, newest first (ADR 0054). */
  listOpenTickets(clientId: string): Promise<OpenTicket[]>;
  /** A tech's tickets from every day, not closed (ADR 0183): their own, or (owners only) `userId`'s. */
  listTickets(userId?: string | null): Promise<TicketTray>;
  /** Close a ticket: its owner's Complete status, written as the tech (ADR 0182). A second click sends the same key. */
  closeTicket(ticketId: number, idempotencyKey: string): Promise<TicketAction>;
  /**
   * Reopen a closed ticket to log a block to it ("Reopen and log"; P9, ADR
   * 0197): its In Progress status set as the block's tech, then the block
   * logged as to any open ticket.
   */
  reopenTicket(
    ticketId: number,
    blockId: string,
    idempotencyKey: string,
    thenLog?: { choices: SubmitChoices; idempotency_key: string },
  ): Promise<TicketAction>;
  /** Close several (the tray's Close selected; ADR 0182): each accepted or refused on its own. */
  closeTickets(ticketIds: number[], idempotencyKey: string): Promise<TicketCloses>;
  /** How a close is going. */
  getTicketAction(actionId: string): Promise<TicketAction>;
  /** Try a failed close again ("Couldn't close: Try again"). */
  retryTicketAction(actionId: string): Promise<TicketAction>;
  /** What submitting the block would write, or what's missing (ADR 0050). */
  previewSubmission(
    blockId: string,
    choices: SubmitChoices,
  ): Promise<SubmissionPreview>;
  /** Submit the block: exactly what the preview shows (ADR 0051). */
  submitBlock(
    blockId: string,
    choices: SubmitChoices,
    idempotencyKey: string,
  ): Promise<Submission>;
  /** The block's submission, or null if it has none. */
  getSubmission(blockId: string): Promise<Submission | null>;
  /** Run a failed submission again, from its last completed step. */
  retrySubmission(blockId: string): Promise<Submission>;
  /** Let a failed submission go, so the block can be logged afresh (#831). */
  startOverSubmission(blockId: string): Promise<StartedOver>;
  /** The signed-in tech's call in progress, or null (AC-2a). */
  getActiveCall(): Promise<ActiveCall | null>;
  /** The calls ringing the signed-in tech's phone (Idea #86). */
  getRingingCalls(): Promise<RingingCall[]>;
  /** Choose the client of the tech's call; its record gets it (AC-2b). */
  chooseCallClient(
    callId: string,
    clientId: string,
    remember: boolean,
  ): Promise<CallChosen>;
  /** Set a block's client; `remember` also learns its number for that client. */
  assignClient(
    blockId: string,
    clientId: string,
    remember: boolean,
  ): Promise<ClientAssignment>;
  dismissBlock(
    blockId: string,
    reason: DismissReason,
    note: string | null,
  ): Promise<BlockState>;
  restoreBlock(blockId: string): Promise<BlockState>;
  /**
   * A Needs you block's Vendor… pane (ADR 0194): remember these of its email
   * domains and call numbers as vendors', and dismiss it as a vendor's. None
   * dismisses just the block. Its tech, or an owner, may.
   */
  markVendor(
    blockId: string,
    domains: readonly string[],
    numbers: readonly string[],
  ): Promise<VendorMarked>;
  setBlockDuration(
    blockId: string,
    seconds: number | null,
  ): Promise<BlockDuration>;
  /** Save what the tech typed in the block's Log form, until it's logged (ADR 0097). */
  saveBlockDraft(
    blockId: string,
    draft: Schemas["BlockDraft"],
  ): Promise<BlockDraftSaved>;
  moveActivity(
    activityId: string,
    toBlockId: string | null,
  ): Promise<ActivityMove>;
  mergeBlocks(blockId: string, withBlockId: string): Promise<BlockMerge>;
  splitBlock(blockId: string, at: string): Promise<BlockSplit>;
  /** Owner only. */
  getSettings(): Promise<AppSettings>;
  /** Remember a vendor's email domain: anyone signed in may (ADR 0132). */
  rememberVendorDomain(domain: string): Promise<{ vendor_email_domains: string[] }>;
  /** Owner only. Changes only the settings named; returns them all. */
  updateSettings(changes: SettingsChanges): Promise<AppSettings>;
  /** Owner only. The IDs each Autotask setting can take, from the last sync. */
  getAutotaskChoices(): Promise<AutotaskChoices>;
  /** Owner only. Every Autotask company, with Axon's attributes and its contracts. */
  listClients(): Promise<ClientMapping[]>;
  /** Owner only. Changes only the fields named. */
  updateClient(id: string, changes: ClientChanges): Promise<ClientRecord>;
  /** Owner only. One billing type for many clients, all or nothing. */
  setBillingType(
    clientIds: readonly string[],
    billingType: BillingType | null,
  ): Promise<ClientRecord[]>;
  /** Owner only. Every ScreenConnect company known, with its client, and suggestions (ADR 0078). */
  listScreenConnectCompanies(): Promise<ScreenConnectCompanies>;
  /** Owner only. A client's one ScreenConnect company, replacing any other, or null to clear it. */
  setScreenConnectCompany(
    clientId: string,
    name: string | null,
  ): Promise<ScreenConnectCompanySet>;
  /** Owner only. A client's numbers: Autotask's, read only, and the app's own (ADR 0078). */
  getClientPhones(clientId: string): Promise<ClientPhones>;
  /** Owner only. Forget one of the app's own numbers for a client. */
  forgetClientPhone(clientId: string, numberId: string): Promise<void>;
  /** Privacy mode's demo copy (ADR 0145): made, once, when it's turned on. Its ID. */
  startDemo(): Promise<string>;
  /** Drop a demo copy: when privacy mode is turned off. */
  dropDemoCopy(id: string): Promise<void>;
  /** Owner only. A client's email domains: Autotask's, read only, and those added here (ADR 0143). */
  getClientEmailDomains(clientId: string): Promise<ClientEmailDomains>;
  /** Owner only. Add an email domain to a client; refused with the reason if it can't be its. */
  addClientEmailDomain(clientId: string, domain: string): Promise<AddedEmailDomain>;
  /** Owner only. Remove one of a client's added email domains. */
  removeClientEmailDomain(clientId: string, domainId: string): Promise<void>;
  /** Owner only. A client's network addresses: firewall, iDRAC and server addresses (Phase 7). */
  getClientNetworkAddresses(clientId: string): Promise<ClientNetworkAddresses>;
  /** Owner only. Add a network address to a client; refused with the reason if it can't be its. */
  addClientNetworkAddress(clientId: string, address: string): Promise<AddedNetworkAddress>;
  /** Owner only. Remove one of a client's network addresses. */
  removeClientNetworkAddress(clientId: string, addressId: string): Promise<void>;
  /** Owner only. Every Datto RMM site, with its client and how (ADR 0080). */
  listDattoSites(): Promise<DattoSites>;
  /** Owner only. The owner's choice of a client's one Datto site, or null to clear it. */
  setDattoSite(clientId: string, uid: string | null): Promise<DattoSiteSet>;
  /** Owner only. Every user with their Autotask resource, and every synced resource. */
  getTechMapping(): Promise<TechMapping>;
  /**
   * One user's own settings, for My settings (ADR 0200): their row, their
   * Autotask resource, and the choices no one else holds. Theirs, or an owner's.
   */
  getOwnSettings(userId: string): Promise<TechMapping>;
  /** Owner only. Map a user to an Autotask resource, or unmap them with null. */
  setAutotaskResource(
    userId: string,
    resourceId: number | null,
  ): Promise<AutotaskResourceSet>;
  /** Owner only. Set a user's SkySwitch extensions (keys "<extension>@<domain>"). */
  setSkySwitchExtensions(
    userId: string,
    keys: readonly string[],
  ): Promise<SkySwitchExtensionsSet>;
  /** Owner only. Set a user's one Datto RMM username, or clear it with null (ADR 0080). */
  setDattoRmmUsername(
    userId: string,
    username: string | null,
  ): Promise<DattoUsernameSet>;
  /** Owner only. Set a user's ScreenConnect username, or clear it with null (roadmap PR-38). */
  setScreenConnectUsername(
    userId: string,
    username: string | null,
  ): Promise<ScreenConnectUsernameSet>;
  /** Owner only. Turn reading a user's mail on or off (roadmap PR-55; ADR 0108). */
  setReadMail(userId: string, readMail: boolean): Promise<ReadMailSet>;
  /** Owner only. Give a user an office address, or a small range, from now (Phase 7; ADR 0158). */
  addOfficeAddress(userId: string, address: string): Promise<OfficeAddress>;
  /** Owner only. Take one of a user's office addresses away, from now; its history stays. */
  endOfficeAddress(userId: string, addressId: string): Promise<void>;
  /**
   * Owner only. A VPN address is the user's, set by the owner: another tech's
   * moves to them, and a flagged one is confirmed (ADR 0179).
   */
  setVpnAddress(userId: string, address: string): Promise<VpnAddress>;
  /** Owner only. Forget one of a user's VPN addresses: it's learned again (ADR 0179). */
  clearVpnAddress(userId: string, addressId: string): Promise<void>;
  /**
   * Owner only. Make these ScreenConnect sessions a user's work computers, whose
   * sessions don't count; none clears them (ADR 0162).
   */
  setWorkComputers(userId: string, sessionIds: readonly string[]): Promise<WorkComputersSet>;
  /**
   * Owner only. Capture a window (ISO instants): SkySwitch's, built by the
   * worker, or ScreenConnect's pushed events, built at once (ADR 0069).
   * Microsoft 365's reads one tech's mail: `userId` names them (roadmap PR-56).
   */
  requestCapture(
    windowStart: string,
    windowEnd: string,
    source?: CaptureSource,
    userId?: string,
  ): Promise<Capture>;
  /** Owner only. The techs whose mail a Microsoft 365 capture can read. */
  listCaptureMailboxes(): Promise<CaptureMailbox[]>;
  /** Owner only. Each integration's health, from what the worker recorded. */
  getIntegrations(): Promise<Integrations>;
  /** Owner only. Put failed SkySwitch records back in line: these, or all of them. */
  retrySkySwitch(ids?: readonly string[]): Promise<Retried>;
  retryScreenConnect(ids?: readonly string[]): Promise<Retried>;
  /** Owner only. The latest captures, newest first. */
  /** The newest captures of `source`: each Health panel its own (Idea #308). */
  listCaptures(source: CaptureSource): Promise<Capture[]>;
  /** Owner only. A ready, clean capture's files, as a zip. */
  downloadCapture(id: string): Promise<Blob>;
  /**
   * Owner only. Stop a SonicWall capture that's still recording: its window
   * ends now, and the worker builds it shortly after (the owner, 2026-10-16).
   */
  stopCapture(id: string): Promise<Capture>;
  /** Owner only. */
  deleteCapture(id: string): Promise<void>;
  /**
   * Owner only (Owner tools, ADR 0098). Each queues one of the worker's jobs;
   * `queued` is false when the same job was already waiting.
   */
  syncAutotaskNow(): Promise<Queued>;
  /**
   * A tech's own only, an owner's anyone's (section 6.2; ADR 0123). Section
   * 13.5: every activity for the filters' one client, across the techs, in order.
   */
  /**
   * A tech's own only, an owner's anyone's (section 6.2; ADR 0123). Section
   * 13.1: each tech's available, captured and logged hours, by day, week or month.
   */
  getUtilizationReport(filters: ReportFilters, by: Grouping): Promise<UtilizationReport>;
  /** The same rows as a CSV file. */
  downloadUtilizationCsv(filters: ReportFilters, by: Grouping): Promise<Blob>;
  /**
   * A tech's own work only, an owner's anyone's (section 6.2; ADR 0123).
   * Section 13.2: each managed client's months, captured and logged, the busiest first.
   */
  getManagedEffort(filters: ReportFilters): Promise<ManagedEffort>;
  /** The same rows as a CSV file. */
  downloadManagedEffortCsv(filters: ReportFilters): Promise<Blob>;
  getClientAudit(filters: ReportFilters): Promise<ClientAudit>;
  /** The same rows as a CSV file. */
  downloadClientAuditCsv(filters: ReportFilters): Promise<Blob>;
  /** A user's local day (YYYY-MM-DD), rebuilt from its activities. */
  rebuildDay(userId: string, day: string): Promise<Queued>;
  /** Owner only: one of Axon Time's emails, to the owner alone, as a preview. */
  previewEmail(kind: EmailPreviewKind, userId?: string): Promise<EmailPreviewQueued>;
  /** SkySwitch's calls of the last `hours` (1 to 168), read again. */
  lookForMissedCalls(hours: number): Promise<Queued>;
  /** ScreenConnect's sessions of the last `hours` (1 to 168), read back. */
  /** `unjoined`: also every session with an event nobody joined (ADR 0136). */
  lookForMissedSessions(hours: number, unjoined?: boolean): Promise<Queued>;
  /** Owner only (ADR 0099). The latest Back up now, and whether the backup container took it. */
  getBackupRequest(): Promise<BackupRequest>;
  /** Ask the backup container for a backup now; `requested` is false when one already waits. */
  requestBackup(): Promise<BackupAsked>;
  /** Owner only (ADR 0206). The backups the backup container last listed, newest first. */
  listBackups(): Promise<BackupList>;
  /** One backup, the encrypted `.age` file as it is. Audited. */
  downloadBackup(file: string): Promise<Blob>;
  /**
   * Owner only (ADR 0209). Check one backup with the age private key pasted for
   * this check alone: sent to the backup container, never stored or logged.
   */
  checkBackup(file: string, key: string): Promise<BackupCheck>;
  /** The latest check of a backup, and what it found, or null. */
  getBackupCheck(): Promise<BackupCheck | null>;
  /**
   * A tech's own only, an owner's anyone's (section 6.2; ADR 0123). Section
   * 13.3: each tech's blocks in the days, in order, with their stats.
   */
  getTechActivityReport(filters: ReportFilters): Promise<TechActivityReport>;
  /** The same rows as a CSV file. */
  downloadTechActivityCsv(filters: ReportFilters): Promise<Blob>;
  /**
   * A tech's own only, an owner's anyone's (section 6.2; ADR 0123). Section
   * 13.4: each tech's blocks still to log, by age, as of now (the days don't apply).
   */
  getUnsubmittedAgingReport(filters: ReportFilters): Promise<UnsubmittedAgingReport>;
  /** The same rows as a CSV file. */
  downloadUnsubmittedAgingCsv(filters: ReportFilters): Promise<Blob>;
  /**
   * A tech's own only, an owner's anyone's (section 6.2; ADR 0185). Each
   * tech's tickets from every day, as of now (the days don't apply), in
   * `states` (none: every one but closed), oldest first.
   */
  getOpenTicketsReport(filters: ReportFilters, states: readonly TicketState[]): Promise<OpenTicketsReport>;
  /** The same rows as a CSV file. */
  downloadOpenTicketsCsv(filters: ReportFilters, states: readonly TicketState[]): Promise<Blob>;
  /** Datto RMM's activity log of the last `hours` (1 to 168), read again (ADR 0135, 0139). */
  lookForMissedDattoActivity(hours: number): Promise<Queued>;
  /** Each read mailbox's Sent Items of the last `hours` (1 to 168), read back (ADR 0140). */
  lookForMissedEmail(hours: number): Promise<Queued>;
  /** Owner only (ADR 0135). The worker's by-hand jobs, each with how it last ran. */
  listOwnerJobs(): Promise<OwnerJob[]>;
  /** Queue the owner's job with this key; `queued` is false when it was already waiting. */
  runOwnerJob(key: OwnerJobKey): Promise<Queued>;
  /** Owner only (ADR 0155). What each of the worker's lanes runs, and what waits behind it. */
  listLanes(): Promise<Lane[]>;
}

function fieldErrorsFrom(detail: unknown): FieldError[] {
  if (!Array.isArray(detail)) {
    return [];
  }
  return detail.flatMap((item: unknown) => {
    if (
      typeof item !== "object" ||
      item === null ||
      !("loc" in item) ||
      !("msg" in item)
    ) {
      return [];
    }
    const { loc, msg } = item;
    if (!Array.isArray(loc) || typeof msg !== "string") {
      return [];
    }
    const at: unknown = loc[1];
    return [
      typeof at === "number" && Number.isInteger(at)
        ? { field: String(loc[0]), message: msg, index: at }
        : { field: String(loc[0]), message: msg },
    ];
  });
}

/** A detail people can read: a string, or a {problems: [...]} list (ADR 0051). */
function readableDetail(detail: unknown): string | null {
  if (typeof detail === "string") {
    return detail;
  }
  if (
    typeof detail === "object" &&
    detail !== null &&
    "problems" in detail &&
    Array.isArray(detail.problems)
  ) {
    const problems = detail.problems.filter(
      (p): p is string => typeof p === "string",
    );
    return problems.length > 0 ? problems.join(" ") : null;
  }
  return null;
}

async function readError(response: Response): Promise<ApiError> {
  try {
    const body: unknown = await response.json();
    if (typeof body === "object" && body !== null && "detail" in body) {
      return new ApiError(
        response.status,
        readableDetail(body.detail),
        fieldErrorsFrom(body.detail),
      );
    }
  } catch {
    // Not JSON: no detail to show.
  }
  return new ApiError(response.status, null);
}

export function createApiClient(
  /** A token for the API; `fresh` asks for a new one, not the cached one (#839). */
  getAccessToken: (fresh?: boolean) => Promise<string>,
  fetchFn: typeof fetch = fetch,
  privacy: PrivacyStore = privacyStore,
  options: {
    /** The API refused even a fresh token: the sign-in needs renewing (#839). */
    onUnauthorized?: () => void;
  } = {},
): ApiClient {
  async function fetchAs(
    method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
    path: string,
    body: unknown,
    demo: string | null,
    fresh = false,
  ): Promise<Response> {
    const token = await getAccessToken(fresh);
    const headers: Record<string, string> = {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    };
    if (demo !== null) {
      headers[PRIVACY_HEADER] = demo;
    }
    const init: RequestInit = { method, headers };
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
      init.body = JSON.stringify(body);
    }
    return fetchFn(path, init);
  }

  // Privacy mode's demo copy (ADR 0145): one made at a time, however many ask.
  let making: Promise<string> | null = null;

  function newDemo(): Promise<string> {
    making ??= (async () => {
      try {
        const response = await fetchAs("POST", "/api/v1/privacy/demo", undefined, null);
        if (!response.ok) {
          throw await readError(response);
        }
        const { id } = (await response.json()) as DemoCopy;
        if (privacy.get() !== null) {
          privacy.set(id);
        } else {
          // Turned off while it was made: it goes now, not in a day.
          void fetchAs("DELETE", `/api/v1/privacy/demo/${encodeURIComponent(id)}`, undefined, null).catch(
            () => undefined,
          );
        }
        return id;
      } finally {
        making = null;
      }
    })();
    return making;
  }

  async function send(
    method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
    path: string,
    body?: unknown,
  ): Promise<Response> {
    const current = privacy.get();
    // In privacy mode, served from its demo copy: one made first if there's none.
    let demo = current === null ? null : current === "on" ? await newDemo() : current;
    let response = await fetchAs(method, path, body, demo);
    if (response.status === 410 && demo !== null && privacy.get() !== null) {
      // The copy is gone (a deploy drops them): a new one, never the real data.
      privacy.set("on");
      demo = await newDemo();
      response = await fetchAs(method, path, body, demo);
    }
    if (response.status === 401) {
      // A token MSAL thinks is good, refused (clock skew, a revoked session,
      // keys rolled over): once more with a fresh one, then say the sign-in
      // needs renewing, so the banner shows rather than "answered 401" (#839).
      response = await fetchAs(method, path, body, demo, true);
      if (response.status === 401) {
        options.onUnauthorized?.();
      }
    }
    if ((privacy.get() !== null) !== (current !== null)) {
      // Privacy mode was turned on or off while this was out: its answer is
      // for the other side, real data in a demo or the copy's after it.
      throw new ApiError(409, PRIVACY_CHANGED);
    }
    if (!response.ok) {
      throw await readError(response);
    }
    return response;
  }

  async function call<T>(
    method: "GET" | "POST" | "PATCH" | "PUT",
    path: string,
    body?: unknown,
  ): Promise<T> {
    return (await (await send(method, path, body)).json()) as T;
  }

  return {
    getMe: () => call<Me>("GET", "/api/v1/me"),
    recordSignIn: () => call<Me>("POST", "/api/v1/me/sign-in"),
    setTheme: (theme) =>
      call<Me>("PUT", "/api/v1/me/theme", {
        theme,
      } satisfies Schemas["ThemeChoice"]),
    getDay: (day, userId) => {
      const path = `/api/v1/days/${encodeURIComponent(day)}`;
      return call<Day>(
        "GET",
        userId
          ? `${path}?${new URLSearchParams({ user_id: userId }).toString()}`
          : path,
      );
    },
    listUsers: () => call<UserSummary[]>("GET", "/api/v1/users"),
    updateUser: (id, changes) =>
      call<UserSummary>("PATCH", `/api/v1/users/${encodeURIComponent(id)}`, changes),
    getTeam: () => call<Team>("GET", "/api/v1/team"),
    listClientChoices: () =>
      call<ClientChoice[]>("GET", "/api/v1/clients/choices"),
    listOpenTickets: (clientId) =>
      call<OpenTicket[]>(
        "GET",
        `/api/v1/clients/${encodeURIComponent(clientId)}/open-tickets`,
      ),
    listTickets: (userId) =>
      call<TicketTray>(
        "GET",
        userId ? `/api/v1/tickets?${new URLSearchParams({ user_id: userId }).toString()}` : "/api/v1/tickets",
      ),
    closeTicket: (ticketId, idempotencyKey) =>
      call<TicketAction>("POST", `/api/v1/tickets/${encodeURIComponent(String(ticketId))}/close`, {
        idempotency_key: idempotencyKey,
      } satisfies Schemas["CloseBody"]),
    reopenTicket: (ticketId, blockId, idempotencyKey, thenLog) =>
      call<TicketAction>("POST", `/api/v1/tickets/${encodeURIComponent(String(ticketId))}/reopen`, {
        block_id: blockId,
        idempotency_key: idempotencyKey,
        ...(thenLog ? { then_log: thenLog } : {}),
      } satisfies Schemas["ReopenBody"]),
    closeTickets: (ticketIds, idempotencyKey) =>
      call<TicketCloses>("POST", "/api/v1/tickets/close", {
        autotask_ticket_ids: ticketIds,
        idempotency_key: idempotencyKey,
      } satisfies Schemas["CloseManyBody"]),
    getTicketAction: (actionId) =>
      call<TicketAction>("GET", `/api/v1/ticket-actions/${encodeURIComponent(actionId)}`),
    retryTicketAction: (actionId) =>
      call<TicketAction>("POST", `/api/v1/ticket-actions/${encodeURIComponent(actionId)}/retry`),
    previewSubmission: (blockId, choices) =>
      call<SubmissionPreview>(
        "POST",
        `/api/v1/blocks/${encodeURIComponent(blockId)}/submission/preview`,
        choices,
      ),
    submitBlock: (blockId, choices, idempotencyKey) =>
      call<Submission>(
        "POST",
        `/api/v1/blocks/${encodeURIComponent(blockId)}/submission`,
        {
          ...choices,
          idempotency_key: idempotencyKey,
        } satisfies Schemas["SubmitBody"],
      ),
    getSubmission: (blockId) =>
      call<Submission | null>(
        "GET",
        `/api/v1/blocks/${encodeURIComponent(blockId)}/submission`,
      ),
    retrySubmission: (blockId) =>
      call<Submission>(
        "POST",
        `/api/v1/blocks/${encodeURIComponent(blockId)}/submission/retry`,
      ),
    startOverSubmission: (blockId) =>
      call<StartedOver>(
        "POST",
        `/api/v1/blocks/${encodeURIComponent(blockId)}/submission/start-over`,
      ),
    getActiveCall: () =>
      call<ActiveCall | null>("GET", "/api/v1/me/active-call"),
    getRingingCalls: () =>
      call<RingingCall[]>("GET", "/api/v1/me/ringing-calls"),
    chooseCallClient: (callId, clientId, remember) =>
      call<CallChosen>(
        "PUT",
        `/api/v1/me/active-call/${encodeURIComponent(callId)}/client`,
        {
          client_id: clientId,
          remember,
        } satisfies Schemas["ChooseClient"],
      ),
    assignClient: (blockId, clientId, remember) =>
      call<ClientAssignment>(
        "PUT",
        `/api/v1/blocks/${encodeURIComponent(blockId)}/client`,
        {
          client_id: clientId,
          remember,
        } satisfies Schemas["ClientAssignment"],
      ),
    dismissBlock: (blockId, reason, note) =>
      call<BlockState>(
        "POST",
        `/api/v1/blocks/${encodeURIComponent(blockId)}/dismiss`,
        { reason, note } satisfies Schemas["Dismissal"],
      ),
    markVendor: (blockId, domains, numbers) =>
      call<VendorMarked>(
        "POST",
        `/api/v1/blocks/${encodeURIComponent(blockId)}/vendor`,
        { domains: [...domains], numbers: [...numbers] } satisfies Schemas["VendorMarking"],
      ),
    restoreBlock: (blockId) =>
      call<BlockState>(
        "POST",
        `/api/v1/blocks/${encodeURIComponent(blockId)}/restore`,
      ),
    mergeBlocks: (blockId, withBlockId) =>
      call<BlockMerge>(
        "POST",
        `/api/v1/blocks/${encodeURIComponent(blockId)}/merge`,
        { block_id: withBlockId } satisfies Schemas["MergeWith"],
      ),
    splitBlock: (blockId, at) =>
      call<BlockSplit>(
        "POST",
        `/api/v1/blocks/${encodeURIComponent(blockId)}/split`,
        { at } satisfies Schemas["SplitAt"],
      ),
    moveActivity: (activityId, toBlockId) =>
      call<ActivityMove>(
        "PUT",
        `/api/v1/activities/${encodeURIComponent(activityId)}/block`,
        {
          block_id: toBlockId,
        } satisfies Schemas["ActivityMove"],
      ),
    setBlockDuration: (blockId, seconds) =>
      call<BlockDuration>(
        "PUT",
        `/api/v1/blocks/${encodeURIComponent(blockId)}/duration`,
        { seconds } satisfies Schemas["DurationChange"],
      ),
    saveBlockDraft: (blockId, draft) =>
      call<BlockDraftSaved>(
        "PUT",
        `/api/v1/blocks/${encodeURIComponent(blockId)}/draft`,
        {
          title: draft.title,
          notes: draft.notes,
        } satisfies Schemas["BlockDraft"],
      ),
    getSettings: () => call<AppSettings>("GET", "/api/v1/settings"),
    rememberVendorDomain: (domain) =>
      call<{ vendor_email_domains: string[] }>("POST", "/api/v1/email/vendor-domains", { domain }),
    updateSettings: (changes) =>
      call<AppSettings>("PATCH", "/api/v1/settings", changes),
    getAutotaskChoices: () =>
      call<AutotaskChoices>("GET", "/api/v1/settings/autotask-choices"),
    listClients: () => call<ClientMapping[]>("GET", "/api/v1/clients"),
    updateClient: (id, changes) =>
      call<ClientRecord>(
        "PATCH",
        `/api/v1/clients/${encodeURIComponent(id)}`,
        changes,
      ),
    setBillingType: (clientIds, billingType) =>
      call<ClientRecord[]>("POST", "/api/v1/clients/billing-type", {
        client_ids: [...clientIds],
        billing_type: billingType,
      } satisfies Schemas["BulkBillingType"]),
    listScreenConnectCompanies: () =>
      call<ScreenConnectCompanies>(
        "GET",
        "/api/v1/clients/screenconnect-companies",
      ),
    setScreenConnectCompany: (clientId, name) =>
      call<ScreenConnectCompanySet>(
        "PUT",
        `/api/v1/clients/${encodeURIComponent(clientId)}/screenconnect-company`,
        { name } satisfies Schemas["ScreenConnectCompanyChange"],
      ),
    getClientPhones: (clientId) =>
      call<ClientPhones>(
        "GET",
        `/api/v1/clients/${encodeURIComponent(clientId)}/phone-numbers`,
      ),
    forgetClientPhone: async (clientId, numberId) => {
      await send(
        "DELETE",
        `/api/v1/clients/${encodeURIComponent(clientId)}/phone-numbers/${encodeURIComponent(numberId)}`,
      );
    },
    startDemo: () => newDemo(),
    dropDemoCopy: async (id) => {
      await send("DELETE", `/api/v1/privacy/demo/${encodeURIComponent(id)}`);
    },
    getClientEmailDomains: (clientId) =>
      call<ClientEmailDomains>(
        "GET",
        `/api/v1/clients/${encodeURIComponent(clientId)}/email-domains`,
      ),
    addClientEmailDomain: (clientId, domain) =>
      call<AddedEmailDomain>(
        "POST",
        `/api/v1/clients/${encodeURIComponent(clientId)}/email-domains`,
        { domain } satisfies Schemas["EmailDomainIn"],
      ),
    removeClientEmailDomain: async (clientId, domainId) => {
      await send(
        "DELETE",
        `/api/v1/clients/${encodeURIComponent(clientId)}/email-domains/${encodeURIComponent(domainId)}`,
      );
    },
    getClientNetworkAddresses: (clientId) =>
      call<ClientNetworkAddresses>(
        "GET",
        `/api/v1/clients/${encodeURIComponent(clientId)}/network-addresses`,
      ),
    addClientNetworkAddress: (clientId, address) =>
      call<AddedNetworkAddress>(
        "POST",
        `/api/v1/clients/${encodeURIComponent(clientId)}/network-addresses`,
        { address } satisfies Schemas["NetworkAddressIn"],
      ),
    removeClientNetworkAddress: async (clientId, addressId) => {
      await send(
        "DELETE",
        `/api/v1/clients/${encodeURIComponent(clientId)}/network-addresses/${encodeURIComponent(addressId)}`,
      );
    },
    listDattoSites: () =>
      call<DattoSites>("GET", "/api/v1/clients/datto-sites"),
    setDattoSite: (clientId, uid) =>
      call<DattoSiteSet>(
        "PUT",
        `/api/v1/clients/${encodeURIComponent(clientId)}/datto-site`,
        {
          uid,
        } satisfies Schemas["DattoSiteChange"],
      ),
    getTechMapping: () => call<TechMapping>("GET", "/api/v1/techs"),
    getOwnSettings: (userId) => call<TechMapping>("GET", `/api/v1/techs/${encodeURIComponent(userId)}`),
    setAutotaskResource: (userId, resourceId) =>
      call<AutotaskResourceSet>(
        "PUT",
        `/api/v1/techs/${encodeURIComponent(userId)}/autotask-resource`,
        {
          autotask_resource_id: resourceId,
        } satisfies Schemas["AutotaskResourceChange"],
      ),
    setSkySwitchExtensions: (userId, keys) =>
      call<SkySwitchExtensionsSet>(
        "PUT",
        `/api/v1/techs/${encodeURIComponent(userId)}/skyswitch-extensions`,
        {
          extensions: [...keys],
        } satisfies Schemas["SkySwitchExtensionsChange"],
      ),
    setScreenConnectUsername: (userId, username) =>
      call<ScreenConnectUsernameSet>(
        "PUT",
        `/api/v1/techs/${encodeURIComponent(userId)}/screenconnect-username`,
        { username } satisfies Schemas["ScreenConnectUsernameChange"],
      ),
    setDattoRmmUsername: (userId, username) =>
      call<DattoUsernameSet>(
        "PUT",
        `/api/v1/techs/${encodeURIComponent(userId)}/datto-rmm-username`,
        { username } satisfies Schemas["DattoUsernameChange"],
      ),
    setReadMail: (userId, readMail) =>
      call<ReadMailSet>(
        "PUT",
        `/api/v1/techs/${encodeURIComponent(userId)}/read-mail`,
        { read_mail: readMail } satisfies Schemas["ReadMailChange"],
      ),
    addOfficeAddress: (userId, address) =>
      call<OfficeAddress>(
        "POST",
        `/api/v1/techs/${encodeURIComponent(userId)}/office-addresses`,
        { address } satisfies Schemas["OfficeAddressAdd"],
      ),
    endOfficeAddress: async (userId, addressId) => {
      await send(
        "DELETE",
        `/api/v1/techs/${encodeURIComponent(userId)}/office-addresses/${encodeURIComponent(addressId)}`,
      );
    },
    setVpnAddress: (userId, address) =>
      call<VpnAddress>(
        "POST",
        `/api/v1/techs/${encodeURIComponent(userId)}/vpn-addresses`,
        { address } satisfies Schemas["VpnAddressSet"],
      ),
    clearVpnAddress: async (userId, addressId) => {
      await send(
        "DELETE",
        `/api/v1/techs/${encodeURIComponent(userId)}/vpn-addresses/${encodeURIComponent(addressId)}`,
      );
    },
    setWorkComputers: (userId, sessionIds) =>
      call<WorkComputersSet>(
        "PUT",
        `/api/v1/techs/${encodeURIComponent(userId)}/work-computers`,
        { session_ids: [...sessionIds] } satisfies Schemas["WorkComputersChange"],
      ),
    requestCapture: (windowStart, windowEnd, source = "skyswitch", userId) =>
      call<Capture>("POST", "/api/v1/captures", {
        window_start: windowStart,
        window_end: windowEnd,
        source,
        ...(userId === undefined ? {} : { user_id: userId }),
      } satisfies Schemas["CaptureRequest"]),
    listCaptureMailboxes: () => call<CaptureMailbox[]>("GET", "/api/v1/captures/mailboxes"),
    getIntegrations: () => call<Integrations>("GET", "/api/v1/integrations"),
    retrySkySwitch: (ids) =>
      call<Retried>(
        "POST",
        "/api/v1/integrations/skyswitch/retry",
        (ids ? { ids: [...ids] } : {}) satisfies Schemas["RetryRequest"],
      ),
    retryScreenConnect: (ids) =>
      call<Retried>(
        "POST",
        "/api/v1/integrations/screenconnect/retry",
        (ids ? { ids: [...ids] } : {}) satisfies Schemas["RetryRequest"],
      ),
    listCaptures: (source) =>
      call<Capture[]>(
        "GET",
        `/api/v1/captures?source=${encodeURIComponent(source)}`,
      ),
    downloadCapture: async (id) =>
      (
        await send("GET", `/api/v1/captures/${encodeURIComponent(id)}/download`)
      ).blob(),
    stopCapture: (id) =>
      call<Capture>("POST", `/api/v1/captures/${encodeURIComponent(id)}/stop`),
    deleteCapture: async (id) => {
      await send("DELETE", `/api/v1/captures/${encodeURIComponent(id)}`);
    },
    syncAutotaskNow: () =>
      call<Queued>("POST", "/api/v1/owner-tools/autotask-sync"),
    previewEmail: (kind, userId) =>
      call<EmailPreviewQueued>("POST", "/api/v1/owner-tools/email-preview", {
        kind,
        user_id: userId ?? null,
      } satisfies Schemas["EmailPreview"]),
    rebuildDay: (userId, day) =>
      call<Queued>("POST", "/api/v1/owner-tools/rebuild-day", {
        user_id: userId,
        day,
      } satisfies Schemas["RebuildDay"]),
    lookForMissedCalls: (hours) =>
      call<Queued>("POST", "/api/v1/owner-tools/missed-calls", {
        hours,
      } satisfies Schemas["MissedCallsPeriod"]),
    lookForMissedSessions: (hours, unjoined = false) =>
      call<Queued>("POST", "/api/v1/owner-tools/missed-sessions", {
        hours,
        unjoined,
      } satisfies Schemas["MissedSessionsPeriod"]),
    getBackupRequest: () =>
      call<BackupRequest>("GET", "/api/v1/owner-tools/backup"),
    requestBackup: () =>
      call<BackupAsked>("POST", "/api/v1/owner-tools/backup"),
    listBackups: () => call<BackupList>("GET", "/api/v1/owner-tools/backups"),
    downloadBackup: async (file) =>
      (
        await send(
          "GET",
          `/api/v1/owner-tools/backups/${encodeURIComponent(file)}/download`,
        )
      ).blob(),
    checkBackup: (file, key) =>
      call<BackupCheck>("POST", `/api/v1/owner-tools/backups/${encodeURIComponent(file)}/check`, {
        key,
      } satisfies Schemas["BackupCheckIn"]),
    getBackupCheck: () => call<BackupCheck | null>("GET", "/api/v1/owner-tools/backup-check"),
    lookForMissedDattoActivity: (hours) =>
      call<Queued>("POST", "/api/v1/owner-tools/missed-datto-activity", {
        hours,
      } satisfies Schemas["MissedDattoPeriod"]),
    lookForMissedEmail: (hours) =>
      call<Queued>("POST", "/api/v1/owner-tools/missed-email", {
        hours,
      } satisfies Schemas["MissedEmailPeriod"]),
    listOwnerJobs: () => call<OwnerJob[]>("GET", "/api/v1/owner-tools/jobs"),
    runOwnerJob: (key) =>
      call<Queued>("POST", `/api/v1/owner-tools/jobs/${encodeURIComponent(key)}`),
    listLanes: () => call<Lane[]>("GET", "/api/v1/owner-tools/lanes"),
    getTechActivityReport: (filters) =>
      call<TechActivityReport>("GET", `/api/v1/reports/tech-activity?${reportQuery(filters)}`),
    downloadTechActivityCsv: async (filters) =>
      (await send("GET", `/api/v1/reports/tech-activity.csv?${reportQuery(filters)}`)).blob(),
    getUnsubmittedAgingReport: (filters) =>
      call<UnsubmittedAgingReport>("GET", `/api/v1/reports/unsubmitted-aging?${agingQuery(filters)}`),
    downloadUnsubmittedAgingCsv: async (filters) =>
      (await send("GET", `/api/v1/reports/unsubmitted-aging.csv?${agingQuery(filters)}`)).blob(),
    getOpenTicketsReport: (filters, states) =>
      call<OpenTicketsReport>("GET", `/api/v1/reports/open-tickets?${openTicketsQuery(filters, states)}`),
    downloadOpenTicketsCsv: async (filters, states) =>
      (await send("GET", `/api/v1/reports/open-tickets.csv?${openTicketsQuery(filters, states)}`)).blob(),
    getUtilizationReport: (filters, by) =>
      call<UtilizationReport>("GET", `/api/v1/reports/utilization?${utilizationQuery(filters, by)}`),
    downloadUtilizationCsv: async (filters, by) =>
      (await send("GET", `/api/v1/reports/utilization.csv?${utilizationQuery(filters, by)}`)).blob(),
    getManagedEffort: (filters) =>
      call<ManagedEffort>("GET", `/api/v1/reports/managed-effort?${reportQuery(filters)}`),
    downloadManagedEffortCsv: async (filters) =>
      (await send("GET", `/api/v1/reports/managed-effort.csv?${reportQuery(filters)}`)).blob(),
    getClientAudit: (filters) => call<ClientAudit>("GET", `/api/v1/reports/client-audit?${reportQuery(filters)}`),
    downloadClientAuditCsv: async (filters) =>
      (await send("GET", `/api/v1/reports/client-audit.csv?${reportQuery(filters)}`)).blob(),
  };
}
